export type TallyBundleCount = { lengthId: string; bundleCount: number };

export type BundleCountsByLengthId = Readonly<Record<string, number>>;

export type RandomBundlesByProductId = Readonly<
  Record<string, BundleCountsByLengthId>
>;

type BundleOfLength = {
  bundleCount: number;
  platformProductsLength: { length: { id: string } };
};

type BundleWithActualLength = {
  platformProductsLength: { length: { actualLengthInches: number } };
};

export function sortedByLength<Bundle extends BundleWithActualLength>(
  bundles: readonly Bundle[]
): Bundle[] {
  return [...bundles].sort(
    (first, second) =>
      first.platformProductsLength.length.actualLengthInches -
      second.platformProductsLength.length.actualLengthInches
  );
}

export function bundleCountsOf(
  bundles: readonly BundleOfLength[]
): BundleCountsByLengthId {
  return Object.fromEntries(
    bundles.map((bundle) => [
      bundle.platformProductsLength.length.id,
      bundle.bundleCount,
    ])
  );
}

export function bundleCountTexts(
  bundleCounts: BundleCountsByLengthId | undefined
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(bundleCounts ?? {}).map(([lengthId, bundleCount]) => [
      lengthId,
      String(bundleCount),
    ])
  );
}

export function isValidBundleCountText(text: string | undefined): boolean {
  if (!text || text.trim() === '') return true;
  const bundleCount = Number(text);
  return Number.isInteger(bundleCount) && bundleCount >= 0;
}

export function hasInvalidBundleCount(
  bundleCountsByLengthId: Record<string, string>,
  candidateLengthIds: readonly string[]
): boolean {
  return candidateLengthIds.some(
    (lengthId) => !isValidBundleCountText(bundleCountsByLengthId[lengthId])
  );
}

export function positiveBundleCounts(
  bundleCountsByLengthId: Record<string, string>,
  candidateLengthIds: readonly string[]
): TallyBundleCount[] {
  return candidateLengthIds
    .map((lengthId) => ({
      lengthId,
      bundleCount: Number(bundleCountsByLengthId[lengthId] ?? ''),
    }))
    .filter(
      (bundle) => Number.isInteger(bundle.bundleCount) && bundle.bundleCount > 0
    );
}
